// client/src/components/pathing_tools/PathingManager.js
import { useEffect, useRef } from 'react';

export function getShapeType(layer) {
  if (typeof window === 'undefined' || !window.L || !layer) return 'polyline';
  const L = window.L;
  if (layer instanceof L.Circle) return 'circle';
  if (layer instanceof L.Rectangle) return 'rectangle';
  if (layer instanceof L.Polygon) return 'polygon';
  return 'polyline';
}

function approxSameLatLng(a, b) {
  if (!a || !b) return false;
  const eps = 1e-9;
  return Math.abs(a.lat - b.lat) < eps && Math.abs(a.lng - b.lng) < eps;
}

function distance(a, b) {
  const dx = b.lng - a.lng;
  const dy = b.lat - a.lat;
  return Math.sqrt(dx * dx + dy * dy);
}

function getLatLngArrayFromLayer(layer, shapeType) {
  if (!layer) return [];

  if (shapeType === 'circle') {
    const center = layer.getLatLng();
    const radius = layer.getRadius();
    if (!center || !radius || radius <= 0) return [];
    const segments = 64;
    const pts = [];
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const dx = radius * Math.cos(angle);
      const dy = radius * Math.sin(angle);
      pts.push({
        lat: center.lat + dy,
        lng: center.lng + dx,
      });
    }
    return pts;
  }

  let latlngs = layer.getLatLngs ? layer.getLatLngs() : [];
  if (!Array.isArray(latlngs)) return [];

  if (latlngs.length && Array.isArray(latlngs[0])) {
    const flat = [];
    latlngs.forEach(part => {
      if (Array.isArray(part)) {
        part.forEach(p => flat.push(p));
      } else {
        flat.push(part);
      }
    });
    latlngs = flat;
  }

  return latlngs;
}

function buildPathInfo(layer) {
  const shapeType = getShapeType(layer);
  const isClosed =
    shapeType === 'circle' ||
    shapeType === 'rectangle' ||
    shapeType === 'polygon';

  let pts = getLatLngArrayFromLayer(layer, shapeType);
  if (!pts || pts.length < 2) {
    return {
      points: [],
      distances: [],
      totalLength: 0,
      shapeType,
      isClosed,
    };
  }

  if (isClosed) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (!approxSameLatLng(first, last)) {
      pts = pts.concat([first]);
    }
  }

  const distances = [0];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = distance(pts[i - 1], pts[i]);
    total += d;
    distances.push(total);
  }

  return {
    points: pts,
    distances,
    totalLength: total,
    shapeType,
    isClosed,
  };
}

export function getPointAtProgress(pathInfo, progress) {
  if (!pathInfo) return null;
  const { points, distances, totalLength, isClosed } = pathInfo;
  if (!points || points.length === 0 || !totalLength || totalLength <= 0) {
    return null;
  }

  let t = progress || 0;
  if (isClosed) {
    t = t - Math.floor(t);
    if (t < 0) t += 1;
  } else {
    if (t < 0) t = 0;
    if (t > 1) t = 1;
  }

  const targetDist = t * totalLength;

  if (targetDist <= 0) return points[0];
  if (targetDist >= totalLength) return points[points.length - 1];

  let i = 1;
  while (i < distances.length && distances[i] < targetDist) {
    i++;
  }

  const prevDist = distances[i - 1];
  const nextDist = distances[i];
  const segLen = nextDist - prevDist || 1;
  const segT = (targetDist - prevDist) / segLen;

  const p0 = points[i - 1];
  const p1 = points[i];

  return {
    lat: p0.lat + (p1.lat - p0.lat) * segT,
    lng: p0.lng + (p1.lng - p0.lng) * segT,
  };
}

export function stepProgress(pathInfo, progress, deltaSeconds, speed, direction) {
  if (!pathInfo) {
    return {
      progress,
      direction,
    };
  }

  const { shapeType, isClosed } = pathInfo;
  const isLine = shapeType === 'polyline';

  const wrapMode =
    isLine &&
    (direction === 'wrap-forward' || direction === 'wrap-backward');

  let dirSign = 1;
  if (
    direction === 'reverse' ||
    direction === 'backward' ||
    direction === 'counterclockwise' ||
    direction === 'wrap-backward'
  ) {
    dirSign = -1;
  }

  const delta = (speed || 0) * deltaSeconds * dirSign;
  let p = (progress || 0) + delta;

  if (isLine) {
    if (wrapMode) {
      if (!Number.isFinite(p)) p = 0;
      p = p - Math.floor(p);
      if (p < 0) p += 1;
    } else {
      if (p > 1) {
        const over = p - 1;
        p = 1 - over;
        dirSign *= -1;
      } else if (p < 0) {
        const over = -p;
        p = over;
        dirSign *= -1;
      }
    }
  } else if (isClosed) {
    if (!Number.isFinite(p)) p = 0;
    p = p - Math.floor(p);
    if (p < 0) p += 1;
  } else {
    if (p < 0) p = 0;
    if (p > 1) p = 1;
  }

  let newDirection = direction || 'forward';
  if (direction === 'clockwise' || direction === 'counterclockwise') {
    newDirection = dirSign >= 0 ? 'clockwise' : 'counterclockwise';
  } else if (direction === 'wrap-forward' || direction === 'wrap-backward') {
    newDirection = dirSign >= 0 ? 'wrap-forward' : 'wrap-backward';
  } else if (direction === 'backward' || direction === 'reverse') {
    newDirection = dirSign >= 0 ? 'forward' : 'backward';
  } else {
    newDirection = dirSign >= 0 ? 'forward' : 'reverse';
  }

  return {
    progress: p,
    direction: newDirection,
  };
}

// Simple HTML escaper so labels can't break the tooltip markup
function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Build HTML that matches the POI label look (black pill, coloured border, pointer)
function getPathTokenLabelHTML(label, colour) {
  const col = colour || '#ffe670';
  const safeLabel = escapeHtml(label || 'Unnamed Zone');

  const innerHtml = `
    <div style="background:#000;box-shadow:0 2px 10px #0006;color:#fff;border-radius:10px;border:3px solid ${col};font-weight:bold;padding:6px 18px;font-size:21px;display:inline-block;text-align:center;min-width:60px;position:relative;opacity:1;">
      ${safeLabel}
      <div style="position:absolute;left:50%;bottom:-15px;transform:translateX(-50%);width:0;height:0;border-left:12px solid transparent;border-right:12px solid transparent;border-top:13px solid ${col};"></div>
    </div>
  `;

  return `
    <div style="transform:scale(var(--poi-label-scale,1));transform-origin:bottom center;">
      ${innerHtml}
    </div>
  `;
}

export function useShapePathing({
  layer,
  enabled,
  autoMove,
  speed,
  progress,
  direction,
  onConfigChange,
  tokenIconUrl,
  tokenColor,
  shapeLabel,
  showLabels = false,
}) {
  const pathInfoRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  const markerRef = useRef(null);

  const configRef = useRef({
    enabled,
    autoMove,
    speed,
    progress,
    direction,
  });

  useEffect(() => {
    configRef.current = {
      enabled,
      autoMove,
      speed,
      progress,
      direction,
    };
  }, [enabled, autoMove, speed, progress, direction]);

  function applyLabel() {
    const marker = markerRef.current;
    if (!marker) return;

    const shouldShowLabel = showLabels && !!shapeLabel && !tokenIconUrl;

    if (shouldShowLabel) {
      if (marker.getTooltip && marker.getTooltip()) {
        marker.unbindTooltip();
      }
      const html = getPathTokenLabelHTML(shapeLabel, tokenColor);
      if (marker.bindTooltip) {
        marker.bindTooltip(html, {
          permanent: true,
          direction: 'top',
          offset: [0, -12],
          className: 'poi-label-tooltip pa-path-token-tooltip',
        });
        if (marker.openTooltip) {
          marker.openTooltip();
        }
      }
    } else {
      if (marker.getTooltip && marker.getTooltip()) {
        marker.unbindTooltip();
      }
    }
  }

  useEffect(() => {
    if (!layer || typeof window === 'undefined' || !window.L) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;
      if (markerRef.current && markerRef.current.remove) {
        const m = markerRef.current;
        if (m._map) m._map.removeLayer(m);
        markerRef.current = null;
      }
      return;
    }

    const L = window.L;
    let cancelled = false;

    function startWhenReady() {
      if (cancelled) return;

      const map = layer._map;
      if (!map) {
        rafRef.current = requestAnimationFrame(startWhenReady);
        return;
      }

      pathInfoRef.current = buildPathInfo(layer);

      function ensureMarker() {
        if (markerRef.current || !configRef.current.enabled) return;
        const cfg = configRef.current;
        const pt =
          getPointAtProgress(pathInfoRef.current, cfg.progress) ||
          (layer.getBounds && layer.getBounds().getCenter
            ? layer.getBounds().getCenter()
            : layer.getLatLng
            ? layer.getLatLng()
            : null);

        if (!pt) return;

        const baseColor = tokenColor || '#ffd500';
        let marker;

        if (tokenIconUrl) {
          const icon = L.icon({
            iconUrl: tokenIconUrl,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            className: 'pa-path-token-icon',
          });
          marker = L.marker(pt, { icon }).addTo(map);
        } else {
          marker = L.circleMarker(pt, {
            radius: 7,
            color: baseColor,
            weight: 2,
            fillColor: baseColor,
            fillOpacity: 0.95,
          }).addTo(map);
        }

        markerRef.current = marker;
        applyLabel();
      }

      function removeMarker() {
        if (markerRef.current && map) {
          map.removeLayer(markerRef.current);
          markerRef.current = null;
        }
      }

      function updateMarkerPosition() {
        if (!markerRef.current || !pathInfoRef.current) return;
        const cfg = configRef.current;
        const pt = getPointAtProgress(
          pathInfoRef.current,
          cfg.progress || 0
        );
        if (pt) markerRef.current.setLatLng(pt);
      }

      function tick(timestamp) {
        if (cancelled) return;

        const cfg = configRef.current;

        if (!cfg.enabled) {
          removeMarker();
          lastTimeRef.current = timestamp;
        } else {
          ensureMarker();

          if (cfg.autoMove && cfg.speed > 0 && pathInfoRef.current) {
            if (lastTimeRef.current == null) {
              lastTimeRef.current = timestamp;
            } else {
              const dt = (timestamp - lastTimeRef.current) / 1000;
              lastTimeRef.current = timestamp;

              const result = stepProgress(
                pathInfoRef.current,
                cfg.progress || 0,
                dt,
                cfg.speed || 0,
                cfg.direction || 'forward'
              );

              if (onConfigChange) {
                onConfigChange(prev => ({
                  ...prev,
                  progress: result.progress,
                  direction: result.direction,
                }));
              }
            }
            updateMarkerPosition();
          } else {
            updateMarkerPosition();
            lastTimeRef.current = timestamp;
          }
        }

        rafRef.current = requestAnimationFrame(tick);
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    startWhenReady();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;
      if (markerRef.current && markerRef.current.remove) {
        const m = markerRef.current;
        if (m._map) m._map.removeLayer(m);
        markerRef.current = null;
      }
    };
  }, [layer, onConfigChange, tokenIconUrl, tokenColor, shapeLabel, showLabels]);

  useEffect(() => {
    applyLabel();
  }, [shapeLabel, showLabels, tokenIconUrl]);
}
